import {
  ApiError,
} from "@airange/shared/api";
import { EXAM_ADMIN_PAGE_PATHS } from "@airange/shared/exam-routes";
import { bootstrapBrowserAppSession } from "@airange/shared/oidc-session";
import { buildBrowserSsoLoginClientContext } from "@airange/shared/sso-login-context";
import { examAdminOpenApiClient } from "../generated/client";
import { ssoCenterOpenApiClient } from "../generated/sso-center/client";
import type {
  AuthBootstrapResponse,
  ChangePasswordPayload,
  LoginPayload,
  LoginResponse,
  UpdateProfilePayload,
} from "./auth-contract";
import {
  completeSsoOidcCallback,
  consumeSsoOidcAuthorizationRedirectPath,
  logoutSsoSession,
  startSsoOidcAuthorization,
} from "./center-api";

export {
  getUserPreferences,
  resetUserPreferences,
  type UserPreferenceRead,
  type UserPreferenceSettings,
  type UserPreferenceUpdate,
  updateUserPreferences,
} from "./auth-preferences-api";

import { unwrapOpenApiResult } from "./openapi-result";
import {
  type AuthLoginSource,
  type AuthMode,
  clearSession,
  setSession,
  setStoredAuthSource,
} from "./session";
import type { SsoLoginPayload } from "./sso-auth-contract";

export type {
  AuthBootstrapResponse,
  AuthProviderStatus,
  ChangePasswordPayload,
  LoginCaptchaChallenge,
  LoginPayload,
  LoginResponse,
  UpdateProfilePayload,
  UserRole,
  UserSession,
} from "./auth-contract";

const SSO_COOKIE_SESSION_MARKER = "exam-sso-cookie-session";

function withClientLoginContext(payload: LoginPayload): LoginPayload {
  if (typeof window === "undefined") return payload;
  const screenWidth = Number(window.screen?.width || 0);
  const screenHeight = Number(window.screen?.height || 0);
  const viewportWidth = Number(window.innerWidth || 0);
  const viewportHeight = Number(window.innerHeight || 0);
  const devicePixelRatio = Number(window.devicePixelRatio || 1);
  return {
    ...payload,
    screen_width: screenWidth > 0 ? screenWidth : undefined,
    screen_height: screenHeight > 0 ? screenHeight : undefined,
    viewport_width: viewportWidth > 0 ? viewportWidth : undefined,
    viewport_height: viewportHeight > 0 ? viewportHeight : undefined,
    device_pixel_ratio: Number.isFinite(devicePixelRatio) ? devicePixelRatio : undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
    language: navigator?.language || undefined,
  };
}

async function unwrapOpenApiVoid(result: {
  data?: unknown;
  error?: unknown;
  response: Response;
}): Promise<void> {
  if (result.response.ok && result.response.status === 204) {
    return;
  }
  await unwrapOpenApiResult(result);
}

async function bootstrapExamAdminAppSession(_fetchImpl: typeof fetch = fetch) {
  const user: AuthBootstrapResponse = await unwrapOpenApiResult(
    await examAdminOpenApiClient.POST("/api/auth/bootstrap"),
  );
  setSession(SSO_COOKIE_SESSION_MARKER, user);
  setStoredAuthSource("sso");
  return user;
}

async function syncCurrentAppSessionFromSso() {
  const bootstrapped = await bootstrapBrowserAppSession<AuthBootstrapResponse>({
    bootstrapAppSession: bootstrapExamAdminAppSession,
    clearSession,
  });
  if (!bootstrapped.currentUser) {
    throw new ApiError(401, "Exam admin app session bootstrap failed");
  }
  return bootstrapped.currentUser;
}

export async function login(payload: LoginPayload, source: AuthLoginSource = "local") {
  if (source === "sso") {
    const ssoLoginBody = {
      username: payload.username,
      password: payload.password,
      client_context: buildBrowserSsoLoginClientContext(),
    } satisfies SsoLoginPayload;
    const response = await unwrapOpenApiResult(
      await ssoCenterOpenApiClient.POST("/api/auth/login", {
        body: ssoLoginBody,
      }),
    );
    const user = await syncCurrentAppSessionFromSso();
    return {
      ...response,
      user,
    } satisfies LoginResponse;
  }
  return unwrapOpenApiResult(
    await examAdminOpenApiClient.POST("/api/auth/login", {
      body: withClientLoginContext(payload),
    }),
  );
}

export async function startSsoLogin(redirectPath: string, prompt: "none" | "login" = "none") {
  await startSsoOidcAuthorization(redirectPath, { prompt });
}

export async function completeSsoLoginCallback(code: string, state: string) {
  const completed = await completeSsoOidcCallback(code, state);
  const user = await syncCurrentAppSessionFromSso();
  return {
    redirectPath: completed.redirectPath,
    user,
  };
}

export function consumeSsoLoginRedirectPath(defaultPath = EXAM_ADMIN_PAGE_PATHS.dashboard) {
  return consumeSsoOidcAuthorizationRedirectPath(defaultPath);
}

export async function getAuthProviderStatus() {
  return unwrapOpenApiResult(await examAdminOpenApiClient.GET("/api/auth/provider-status"));
}

export async function getLoginCaptcha() {
  return unwrapOpenApiResult(await examAdminOpenApiClient.GET("/api/auth/captcha"));
}

export async function getCurrentUser(_source: AuthLoginSource = "local") {
  return unwrapOpenApiResult(await examAdminOpenApiClient.GET("/api/auth/me"));
}

export async function changePassword(payload: ChangePasswordPayload) {
  return unwrapOpenApiResult(
    await examAdminOpenApiClient.POST("/api/auth/change-password", {
      body: payload,
    }),
  );
}

export async function updateProfile(payload: UpdateProfilePayload) {
  return unwrapOpenApiResult(
    await examAdminOpenApiClient.POST("/api/auth/profile", {
      body: payload,
    }),
  );
}

export async function logout(source: AuthLoginSource = "local") {
  if (source === "sso") {
    return logoutSsoSession();
  }
  return unwrapOpenApiVoid(await examAdminOpenApiClient.POST("/api/auth/logout"));
}
